/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useMemo } from "react"
import { Item, ModifierGroup } from "types/models/menu"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { ItemPreview } from "components/previews"
import { renderModifierGroupRule } from "utils/misc"
import { PreviewDrawer } from "./PreviewDrawer"

interface ItemPreviewDrawerProps {
  previewItem?: Item
  showPreview: boolean
  onClosePreview: any
  modifierGroups?: ModifierGroup[]
}

export const ItemPreviewDrawer: FC<ItemPreviewDrawerProps> = ({
  previewItem,
  modifierGroups,
  showPreview,
  onClosePreview,
}: ItemPreviewDrawerProps) => {
  const textMap = useTranslationsMap({
    pickX: "menu.modifierGroup.preview.pickX",
    pickXtoY: "menu.modifierGroup.preview.pickXtoY",
    pickUpToXOptional: "menu.modifierGroup.preview.pickUpToXoptional",
  })

  const isMaxMultipleSelection = (modifierGroup: ModifierGroup) =>
    !renderModifierGroupRule(modifierGroup, textMap)?.requireExactly &&
    modifierGroup.selectionRangeMin === modifierGroup.selectionRangeMax

  const initSelections = useMemo(() => {
    const initialSelections: {
      [key: string]: number[]
    } = {}
    modifierGroups?.forEach(modifierGroup => {
      initialSelections[modifierGroup.modifierGroupID] = isMaxMultipleSelection(
        modifierGroup,
      )
        ? modifierGroup.modifiers.map(modifier => {
            return modifier.priceInMin
          })
        : []
    })
    return initialSelections
  }, [modifierGroups])

  return (
    <PreviewDrawer
      title={useTranslation("menu.item.modal.previewItem")}
      visible={showPreview}
      onClose={onClosePreview}
    >
      <ItemPreview
        item={previewItem}
        modifierGroups={modifierGroups}
        initSelections={initSelections}
      />
    </PreviewDrawer>
  )
}
